import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Basic Lawn Mowing",
  "Mulching & Bed Refresh",
  "Hedge Trimming & Pruning",
  "Weed Control & Fertilization",
  "Edging & Trimming",
  "Seasonal Cleanup",
  "Garden Design & Renovations",
  "Full Property Care"
];

export const ContactPage = () => {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const [quoteForm, setQuoteForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    property_size: "",
    services: [],
    frequency: "",
    additional_details: "",
    preferred_contact: "phone"
  });

  const [loading, setLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (quoteForm.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, quoteForm);
      toast.success("Quote request submitted! Damion will contact you shortly.");
      setQuoteForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        property_size: "",
        services: [],
        frequency: "",
        additional_details: "",
        preferred_contact: "phone"
      });
    } catch (error) {
      toast.error("Failed to submit quote request. Please try again.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    setQuoteForm(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-heading text-gray-900 mb-4">
              Get in <span className="text-primary">Touch</span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Ready to transform your property? Contact us for a free quote or 
              reach out with any questions. We're here to help!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading text-gray-900 mb-1">Call or Text</h3>
                      <a href="tel:7134999372" className="text-gray-700 hover:text-primary transition-colors">
                        (713) 499-9372
                      </a>
                      <p className="text-gray-500 text-sm mt-1">Ask for Damion</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading text-gray-900 mb-1">Email Us</h3>
                      <a href="mailto:info@elitebladelgc.com" className="text-gray-700 hover:text-primary transition-colors">
                        info@elitebladelgc.com
                      </a>
                      <p className="text-gray-500 text-sm mt-1">We respond within 24 hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading text-gray-900 mb-1">Service Area</h3>
                      <p className="text-gray-700">Greater Houston Area</p>
                      <p className="text-gray-500 text-sm mt-1">Houston, Katy, Sugar Land & more</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading text-gray-900 mb-1">Business Hours</h3>
                      <p className="text-gray-700">Mon - Sat: 7am - 7pm</p>
                      <p className="text-gray-500 text-sm mt-1">Sunday by appointment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Info */}
              <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                <h3 className="text-lg font-heading text-gray-900 mb-4">Why Contact Us?</h3>
                <ul className="space-y-3">
                  {[
                    "Free property assessments",
                    "No-obligation quotes",
                    "Fast response times",
                    "Flexible scheduling"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center text-gray-700 text-sm">
                      <CheckCircle className="h-4 w-4 text-primary mr-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-gray-200 shadow-md">
                <CardContent className="p-6 md:p-8">
                  <Tabs defaultValue="contact" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-gray-100 mb-8">
                      <TabsTrigger 
                        value="contact" 
                        data-testid="tab-contact"
                        className="font-accent data-[state=active]:bg-primary data-[state=active]:text-white"
                      >
                        Quick Contact
                      </TabsTrigger>
                      <TabsTrigger 
                        value="quote" 
                        data-testid="tab-quote"
                        className="font-accent data-[state=active]:bg-accent data-[state=active]:text-black"
                      >
                        Request Quote
                      </TabsTrigger>
                    </TabsList>

                    {/* Simple Contact Form */}
                    <TabsContent value="contact">
                      <form onSubmit={handleContactSubmit} data-testid="contact-form">
                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label htmlFor="contact-name" className="text-gray-700">Name</Label>
                              <Input
                                id="contact-name"
                                data-testid="contact-name"
                                placeholder="Your name"
                                value={contactForm.name}
                                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="contact-email" className="text-gray-700">Email</Label>
                              <Input
                                id="contact-email"
                                data-testid="contact-email"
                                type="email"
                                placeholder="your@email.com"
                                value={contactForm.email}
                                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary"
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact-phone" className="text-gray-700">Phone</Label>
                            <Input
                              id="contact-phone"
                              data-testid="contact-phone"
                              type="tel"
                              placeholder="(123) 456-7890"
                              value={contactForm.phone}
                              onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="contact-message" className="text-gray-700">Message</Label>
                            <Textarea
                              id="contact-message"
                              data-testid="contact-message"
                              placeholder="How can we help you?"
                              rows={5}
                              value={contactForm.message}
                              onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-primary resize-none"
                              required
                            />
                          </div>
                          <Button 
                            type="submit" 
                            data-testid="contact-submit"
                            disabled={loading}
                            className="w-full bg-primary hover:bg-primary/90 text-white font-accent py-6 text-lg"
                          >
                            {loading ? "Sending..." : "Send Message"}
                            <Send className="ml-2 h-5 w-5" />
                          </Button>
                        </div>
                      </form>
                    </TabsContent>

                    {/* Detailed Quote Form */}
                    <TabsContent value="quote">
                      <form onSubmit={handleQuoteSubmit} data-testid="quote-form">
                        <div className="space-y-6">
                          {/* Personal Info */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label htmlFor="quote-name" className="text-gray-700">Name *</Label>
                              <Input
                                id="quote-name"
                                data-testid="quote-name"
                                placeholder="Your name"
                                value={quoteForm.name}
                                onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-accent focus:ring-accent"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="quote-email" className="text-gray-700">Email *</Label>
                              <Input
                                id="quote-email"
                                data-testid="quote-email"
                                type="email"
                                placeholder="your@email.com"
                                value={quoteForm.email}
                                onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-accent focus:ring-accent"
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label htmlFor="quote-phone" className="text-gray-700">Phone *</Label>
                              <Input
                                id="quote-phone"
                                data-testid="quote-phone"
                                type="tel"
                                placeholder="(123) 456-7890"
                                value={quoteForm.phone}
                                onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-accent focus:ring-accent"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="quote-address" className="text-gray-700">Property Address *</Label>
                              <Input
                                id="quote-address"
                                data-testid="quote-address"
                                placeholder="123 Main St, Houston, TX"
                                value={quoteForm.address}
                                onChange={(e) => setQuoteForm({...quoteForm, address: e.target.value})}
                                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-accent focus:ring-accent"
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <Label htmlFor="quote-size" className="text-gray-700">Property Size *</Label>
                              <Select 
                                value={quoteForm.property_size} 
                                onValueChange={(value) => setQuoteForm({...quoteForm, property_size: value})}
                              >
                                <SelectTrigger 
                                  data-testid="quote-size" 
                                  className="bg-white border-gray-300 text-gray-900"
                                >
                                  <SelectValue placeholder="Select size" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-200">
                                  <SelectItem value="small">Small (under 1/4 acre)</SelectItem>
                                  <SelectItem value="medium">Medium (1/4 - 1/2 acre)</SelectItem>
                                  <SelectItem value="large">Large (1/2 - 1 acre)</SelectItem>
                                  <SelectItem value="xlarge">X-Large (1+ acre)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="quote-frequency" className="text-gray-700">Service Frequency *</Label>
                              <Select 
                                value={quoteForm.frequency} 
                                onValueChange={(value) => setQuoteForm({...quoteForm, frequency: value})}
                              >
                                <SelectTrigger 
                                  data-testid="quote-frequency" 
                                  className="bg-white border-gray-300 text-gray-900"
                                >
                                  <SelectValue placeholder="Select frequency" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-200">
                                  <SelectItem value="one-time">One-Time Service</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="bi-weekly">Bi-Weekly</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Services Selection */}
                          <div className="space-y-3">
                            <Label className="text-gray-700">Services Needed *</Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {serviceOptions.map((service) => (
                                <div 
                                  key={service} 
                                  className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary/50 transition-colors"
                                >
                                  <Checkbox
                                    id={`service-${service}`}
                                    data-testid={`quote-service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                                    checked={quoteForm.services.includes(service)}
                                    onCheckedChange={() => toggleService(service)}
                                    className="border-gray-400 data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                                  />
                                  <Label 
                                    htmlFor={`service-${service}`} 
                                    className="text-gray-700 cursor-pointer text-sm"
                                  >
                                    {service}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Preferred Contact */}
                          <div className="space-y-2">
                            <Label className="text-gray-700">Preferred Contact Method</Label>
                            <Select 
                              value={quoteForm.preferred_contact} 
                              onValueChange={(value) => setQuoteForm({...quoteForm, preferred_contact: value})}
                            >
                              <SelectTrigger 
                                data-testid="quote-contact-method" 
                                className="bg-white border-gray-300 text-gray-900"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200">
                                <SelectItem value="phone">Phone Call</SelectItem>
                                <SelectItem value="text">Text Message</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Additional Details */}
                          <div className="space-y-2">
                            <Label htmlFor="quote-details" className="text-gray-700">Additional Details</Label>
                            <Textarea
                              id="quote-details"
                              data-testid="quote-details"
                              placeholder="Tell us more about your property or specific needs..."
                              rows={4}
                              value={quoteForm.additional_details}
                              onChange={(e) => setQuoteForm({...quoteForm, additional_details: e.target.value})}
                              className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-accent focus:ring-accent resize-none"
                            />
                          </div>

                          <Button 
                            type="submit" 
                            data-testid="quote-submit"
                            disabled={loading}
                            className="w-full bg-accent hover:bg-accent/90 text-black font-accent py-6 text-lg"
                          >
                            {loading ? "Submitting..." : "Request Free Quote"}
                            <Send className="ml-2 h-5 w-5" />
                          </Button>
                        </div>
                      </form>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section data-testid="map-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading text-gray-900 mb-4">
              Service <span className="text-accent">Area</span>
            </h2>
            <p className="text-gray-600">
              We proudly serve the Greater Houston area and surrounding communities.
            </p>
          </div>
          <div className="aspect-video rounded-lg overflow-hidden border border-gray-200 shadow-lg">
            <iframe
              data-testid="service-area-map"
              title="Elite Blade Service Area - Houston, TX"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d443088.1180786904!2d-95.68196373281249!3d29.81737779999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1703000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
